/* eslint-disable @typescript-eslint/naming-convention */
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import ProfileImage, { type ProfileImageGivingUserProfile } from '@brands/Components/ProfileImage/ProfileImage';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import AddEmailModal from '@brands/Dashboard/Dashboard/SuperAdmin/Components/AddEmailModal/AddEmailModal';
import ReadOnlyDependent from '@brands/Dashboard/MyProfile/Patient/Sections/Dependent/ReadOnlyDependent';
import ReadOnlyOrganizations from '@brands/Dashboard/MyProfile/Patient/Sections/Organizations/ReadOnlyOrganizations';
import ValidateAddressModal from '@brands/Dashboard/MyProfile/Patient/Sections/ValidateAddressModal/ValidateAddressModal';
import { MyProfileSubmit } from '@brands/Dashboard/MyProfile/Patient/utils/types';
import { updateData } from '@brands/Dashboard/MyProfile/Patient/utils/utilsFunctions';
import {
  hasBasicInfoChanged,
  hasContactInfoChanged,
  hasLicenseChanged,
  hasProviderProfileChanged,
} from '@brands/Dashboard/MyProfile/Provider/Sections/helpers';
import { ProviderMyProfileSubmit } from '@brands/Dashboard/MyProfile/Provider/utils/types';
import ChangePasswordModal from '@brands/Dashboard/Sidenav/SidenavModals/ChangePasswordModal/ChangePasswordModal';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import ProviderProfileAdminSection from './Sections/ProviderProfileAdminSection';
import { RegisterMediaPayload } from '@brands/services/communication/registersConversationMedia';
import { activateUserById } from '@brands/services/identity/activateUserById';
import { confirmAvatarMedia } from '@brands/services/identity/confirmAvatarMedia';
import { createBasicInformation } from '@brands/services/identity/createBasicInformation';
import { createProviderLicense } from '@brands/services/identity/createProviderLicense';
import { createProviderProfile } from '@brands/services/identity/createProviderProfile';
import { deleteAvatar } from '@brands/services/identity/deleteAvatar';
import { deleteProviderLicense } from '@brands/services/identity/deleteProviderLicense';
import { getCurrentOrganization } from '@brands/services/identity/getCurrentOrganization';
import { getCurrentProfile } from '@brands/services/identity/getCurrentProfile';
import { getMyOrganizations } from '@brands/services/identity/getMyOrganizations';
import { getUserById } from '@brands/services/identity/getUserById';
import { listAllClientTags } from '@brands/services/identity/listAllClientTags';
import { scopeClientTagOptionsToActor } from '@brands/services/identity/clientTagActorScope';
import {
  getNetworkLabel,
  getNetworkOptionsForRole,
  isNetworkManagedByD1,
  listNetworkOptions,
  NetworkOption,
} from '@brands/services/identity/networkOptions';
import { updateUserNetwork } from '@brands/services/identity/updateUserNetwork';
import { listAllLanguages } from '@brands/services/identity/listAllLanguages';
import { registerAvatarMedia } from '@brands/services/identity/registerAvatarMedia';
import { reinviteUserById } from '@brands/services/identity/reinviteUserById';
import { saveBasicInformation } from '@brands/services/identity/saveBasicInformation';
import { saveProviderProfile } from '@brands/services/identity/saveProviderProfile';
import {
  BasicInfo,
  ClientTag,
  Organization,
  ProviderProfile,
  UserProfile,
  UserRoleName,
} from '@brands/services/identity/types/UserProfile';
import { updateUserClientTag } from '@brands/services/identity/updateUserClientTag';
import { useDivisionScopeActor } from '@brands/hooks/useDivisionScopeActor';
import {
  getClientDivisionOptionGroups,
  hasUnfilledRequiredDivisionGroup,
  scopeDivisionGroupsToClientAdmin,
} from '@brands/services/identity/clientDivisionOptions';
import { updateMe } from '@brands/services/identity/updateMe';
import { updateProviderLicense } from '@brands/services/identity/updateProviderLicense';
import { updateVerifiedEmail } from '@brands/services/identity/updateUserEmail';
import { selectAuth } from '@brands/store/selectors/auth';
import { setUser } from '@brands/store/slices/authSlice';
import { setProviderMyProfile } from '@brands/store/slices/providerMyProfileSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { genderIdentity, genderOptions, pronounsOptions } from '@brands/Utils/selectButtonOptions';
import { OptionInsurance, OptionLanguage, specialtyOptions, stateOptions } from '@brands/Utils/selectOptions';
import { UnifiedProfileSubmit } from '@brands/Utils/types';
import { validateAddress, ValidationModalInterface } from '@brands/Utils/validateAddress';
import { yupResolver } from '@hookform/resolvers/yup';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import ConfirmationModal from '@brands/Components/Modals/ConfirmationModal/ConfirmationModal';
import useUnlockAccount from '@brands/Dashboard/MyProfile/Components/useUnlockAccount';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField as TextFieldMUI,
} from '@mui/material';
import { ArrowDropDownIcon, ClearIcon } from '@mui/x-date-pickers';
import cn from 'classnames';
import dayjs from 'dayjs';
import { E164Number, parsePhoneNumber } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import moment from 'moment';
import React, { KeyboardEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { IoAdd, IoChevronBack, IoChevronDown, IoEllipsisVertical } from 'react-icons/io5';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import addNewUserStyles from '../../Dashboard/Collector/AddNewUser/addNewUser.module.scss';
import collectorShellStyles from '../../Dashboard/Collector/EditUser/collectorEditUser.module.scss';
import styles from './styles.module.scss';
import { validationSchema } from './utils/validationSchema';
import MuiLongTextField from '@brands/Components/Inputs/MuiLongTextField/MuiLongTextField';

type SpecialtyOption = {
  label: string;
  value: string;
};

const Profile = ({
  isProfileCard,
  formRef,
  setIsDirty,
  myProfileForm,
  setMyProfileForm,
  insurancePlanName,
  thisUser,
  setThisUser,
  setCurrentAvatarURL,
  setUserLoaded,
  userLoaded,
  reviewProfile: reviewProfileProp,
  loadingUser,
  resetEmailSent = false,
  setResetPasswordConfirmation,
  setChangeUserStatusFlag,
  userId,
}: {
  isProfileCard: boolean;
  formRef: React.RefObject<HTMLFormElement>;
  setIsDirty: (value: boolean) => void;
  myProfileForm: ProviderMyProfileSubmit;
  setMyProfileForm: React.Dispatch<React.SetStateAction<ProviderMyProfileSubmit | null>>;
  insurancePlanName: OptionInsurance[];
  thisUser: UserProfile;
  setThisUser: React.Dispatch<React.SetStateAction<UserProfile>>;
  setCurrentAvatarURL: React.Dispatch<React.SetStateAction<string>>;
  setUserLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  userLoaded: boolean;
  reviewProfile?: boolean;
  loadingUser: boolean;
  resetEmailSent?: boolean;
  setResetPasswordConfirmation?: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      userState: string;
    }>
  >;
  setChangeUserStatusFlag?: React.Dispatch<React.SetStateAction<string>>;
  userId?: string;
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);
  const divisionScopeActor = useDivisionScopeActor();
  const {
    showUnlockAccount,
    isUnlocking,
    unlockConfirmOpen,
    openUnlockConfirm,
    closeUnlockConfirm,
    handleUnlockAccount,
  } = useUnlockAccount(thisUser, setThisUser);
  const { state } = useLocation();
  const roleNavigate = useRoleNavigate();

  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [userHeaderMenuAnchor, setUserHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [displayEmailModal, setDisplayEmailModal] = useState(false);
  const [image, setImage] = React.useState<string>();
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [avatarAction, setAvatarAction] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [validationResponseId, setValidationResponseId] = useState('');
  const [loading, setLoading] = useState(false);
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [clientTagOptions, setClientTagOptions] = useState<OptionLanguage[]>([]);
  const [clientTagCatalog, setClientTagCatalog] = useState<ClientTag[]>([]);
  const [networkOptions, setNetworkOptions] = useState<NetworkOption[]>([]);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isReturnToSelectPatient, setReturnToSelectPatient] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [educationInputValue, setEducationInputValue] = useState('');
  const [specialtyInputValue, setSpecialtyInputValue] = useState('');
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [showValidationAddressModal, setShowValidationAddressModal] = useState<ValidationModalInterface>({
    isOpen: false,
    title: '',
    whatYouEntered: '',
    recommended: false,
    recommendedAddress: '',
    onClose: undefined,
    onConfirmChange: undefined,
    onKeepCurrentAddress: undefined,
  });
  // Required for every role once the field is actually on the page (mirrors the
  // `clientTagOptions.length > 0` visibility check on the Client tags select) --
  // requiredness used to be gated to CareAdvisor/Collector/OrganizationAdmin only.
  const clientTagsRequired = useMemo(() => clientTagOptions.length > 0, [clientTagOptions]);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
    getValues,
    reset,
    control,
  } = useForm<ProviderMyProfileSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema(false, false, clientTagsRequired)),
  });

  const watchedValues = useWatch({ control });
  const reviewProfile = reviewProfileProp ?? false;

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);

  const fetchLanguageOptions = async (): Promise<void> => {
    try {
      setLoading(true);
      const data = await listAllLanguages();
      const transformedData = data.map((language) => ({
        value: language.locale,
        label: language.name,
        id: language.id,
      }));
      setLanguageOptions(transformedData);
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientTagOptions = async (): Promise<void> => {
    if (userInfo.role.name !== UserRoleName.Provider) {
      try {
        const { nodes: tags } = await listAllClientTags();
        const withCode = (tags || []).filter((t: ClientTag) => Boolean(t.code));
        const mapped = withCode.map((t: ClientTag) => ({
          value: String(t.id),
          label: t.name,
          id: String(t.id),
        }));
        setClientTagOptions(scopeClientTagOptionsToActor(mapped, userInfo));
        setClientTagCatalog(withCode);
      } catch (error) {
        displayErrorDetails(error);
      }
    }
  };

  const fetchNetworkOptions = async (): Promise<void> => {
    if (userInfo.role.name === UserRoleName.Admin || userInfo.role.name === UserRoleName.SuperAdmin) {
      try {
        const catalog = await listNetworkOptions(userInfo.role.name);
        setNetworkOptions(getNetworkOptionsForRole(catalog, UserRoleName.Provider));
      } catch (error) {
        displayErrorDetails(error);
      }
    }
  };

  useEffect(() => {
    fetchLanguageOptions();
    fetchClientTagOptions();
    fetchNetworkOptions();
  }, []);

  useEffect(() => {
    if (myProfileForm && Object.keys(myProfileForm).length > 0) {
      reset(structuredClone(myProfileForm));
    }
  }, [myProfileForm, reset]);

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const orgs = await getMyOrganizations();
      setOrganizations(orgs);
      const currentOrg = await getCurrentOrganization();
      setCurrentOrganization(currentOrg);
      setSelectedOrgId(currentOrg?.id?.toString() ?? '');
    } catch (err) {
      displayErrorDetails(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    const anyChange = !isEqual(myProfileForm, watchedValues) || avatarAction !== '';
    setIsDirty?.(anyChange);
  }, [watchedValues, avatarAction, myProfileForm, setIsDirty]);

  const triggerProfileAvatar = React.useCallback(
    (avatarUrl: string) => {
      setValue('profile_img', avatarUrl, { shouldValidate: true });
      setMyProfileForm((prev) =>
        prev
          ? {
              ...prev,
              profile_img: avatarUrl,
            }
          : prev,
      );
    },
    [setValue, setMyProfileForm],
  );

  const onInvalid = (err: any): void => {
    const error = Object.values(err)[0] as any;
    if (error) {
      displayErrorDetails(error);
    }
  };

  const mapUserToProviderProfileForm = useCallback(
    (user: UserProfile): ProviderMyProfileSubmit => {
      const providerProfile = user.provider_profile;
      const basicInfo = user.basic_info;
      const address = providerProfile?.address ?? user.address;

      const mappedLanguages: OptionLanguage[] =
        basicInfo?.languages
          ?.map((language) => {
            if (typeof language === 'string') {
              const found = languageOptions.find(
                (option) =>
                  option.id?.toString() === language || option.value === language || option.label === language,
              );

              return found
                ? {
                    id: found.id,
                    value: found.value,
                    label: found.label,
                  }
                : null;
            }

            const found = languageOptions.find(
              (option) =>
                option.id?.toString() === language.id?.toString() ||
                option.value === language.locale ||
                option.label === language.name,
            );

            return found
              ? {
                  id: found.id,
                  value: found.value,
                  label: found.label,
                }
              : {
                  id: language.id,
                  value: language.locale,
                  label: language.name,
                };
          })
          .filter((item): item is OptionLanguage => Boolean(item)) || [];

      return {
        profile_img: user.picture_media_id || null,
        providerID: user.id || 0,
        first_name: basicInfo?.first_name || '',
        middle_name: basicInfo?.middle_name || '',
        last_name: basicInfo?.last_name || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        address1: address?.address1 || '',
        address2: address?.address2 || '',
        zip_code: address?.zip_code || '',
        city: address?.city || '',
        state: address?.state || '',
        date_of_birth: basicInfo?.date_of_birth || '',
        pronouns: basicInfo?.pronouns || '',
        gender: basicInfo?.gender || '',
        gender_identity: basicInfo?.gender_identity || '',
        language: mappedLanguages,
        languages: basicInfo?.languages || [],
        licence: '',
        licences:
          providerProfile?.licenses?.map((license) => ({
            id: license.id,
            state: license.state || '',
            number: license.number || '',
            expiry_date: license.expiry_date ? moment(license.expiry_date).format('MM/DD/YYYY') : '',
            insurance_carriers:
              license.insurance_carriers?.map((carrier) => ({
                insurance_carrier: carrier.insurance_carrier,
                insurance_carrier_id: carrier.insurance_carrier_id ?? carrier.insurance_carrier?.id,
                effective_date: carrier.effective_date ? moment(carrier.effective_date).format('MM/DD/YYYY') : '',
                recredential_date: carrier.recredential_date
                  ? moment(carrier.recredential_date).format('MM/DD/YYYY')
                  : '',
              })) || [],
          })) || [],
        credentials: providerProfile?.credentials || '',
        dea_number: providerProfile?.dea_number || '',
        national_provider_id: providerProfile?.national_provider_id || '',
        education: providerProfile?.education || [],
        specialty:
          providerProfile?.specialty?.map((item) => ({
            label: item,
            value: item,
          })) || [],
        biography: providerProfile?.biography || '',
        providerPlan:
          providerProfile?.plan?.map((item) => ({
            label: item,
            value: item,
          })) || [],
        plan: providerProfile?.plan || [],
        availability: providerProfile?.availability || [],
        npi: providerProfile?.npi || '',
        additional_credentials: providerProfile?.additional_credentials || '',
        organizations: user.organizations || [],
        client_tags: user.clients,
        client_divisions: user.divisions,
        network: user.network ?? null,
      };
    },
    [languageOptions],
  );

  const callSaveProfile = async (profileData: UnifiedProfileSubmit): Promise<void> => {
    setIsSavingProfile(true);
    setLoading(true);

    const { profile_img: profileImg, ...otherData } = profileData as ProviderMyProfileSubmit;

    try {
      if (avatarPayload && avatarFile && avatarAction === 'Upload') {
        const res = await registerAvatarMedia(avatarPayload);
        const mediaId = res.files[0].upload_id;

        await fetch(res.files[0].upload_url, {
          method: 'PUT',
          body: avatarFile,
          headers: { 'content-type': avatarFile.type },
        });

        await confirmAvatarMedia(mediaId, Number(thisUser?.id));
        triggerProfileAvatar(res.files[0].upload_url);
      } else if (avatarAction === 'Remove') {
        await deleteAvatar(Number(thisUser?.id));
        triggerProfileAvatar('');
      }

      let updatedUser: UserProfile | undefined;

      if ('email' in profileData && thisUser?.email !== profileData.email) {
        updatedUser = await updateVerifiedEmail(Number(thisUser?.id) || 0, {
          email: profileData.email as string,
        });
      }

      const normalizedLanguages = Array.isArray(otherData.language)
        ? otherData.language
            .map((language) => {
              if (typeof language === 'string' || typeof language === 'number') {
                return String(language);
              }

              if (language?.id !== undefined && language?.id !== null) {
                return String(language.id);
              }

              return null;
            })
            .filter((id): id is string => Boolean(id))
        : [];

      const basicInfoPayload: BasicInfo = {
        first_name: otherData.first_name || '',
        middle_name: otherData.middle_name || '',
        last_name: otherData.last_name || '',
        gender: otherData.gender || '',
        date_of_birth: otherData.date_of_birth
          ? moment(otherData.date_of_birth, ['MM/DD/YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : '',
        pronouns: otherData.pronouns || '',
        gender_identity: otherData.gender_identity || '',
        languages: normalizedLanguages,
      };

      if (hasBasicInfoChanged(thisUser, basicInfoPayload)) {
        updatedUser =
          thisUser.basic_info !== null
            ? await saveBasicInformation(basicInfoPayload, thisUser.id)
            : await createBasicInformation(basicInfoPayload, thisUser.id);
      }

      const updateProfileData = {
        education: (otherData as ProviderMyProfileSubmit).education,
        specialty: ((otherData as ProviderMyProfileSubmit).specialty || []).map((option) => option.value),
        biography: (otherData as ProviderMyProfileSubmit).biography,
        additional_credentials: (otherData as ProviderMyProfileSubmit).additional_credentials,
        address: {
          address1: otherData.address1,
          address2: otherData.address2,
          city: otherData.city,
          state: otherData.state,
          zip_code: otherData.zip_code,
        },
        ...(reviewProfile && { national_provider_id: (otherData as ProviderMyProfileSubmit).national_provider_id }),
        ...(reviewProfile && { credentials: (otherData as ProviderMyProfileSubmit).credentials }),
      };

      const updateMePayload = {
        ...(otherData.phone_number
          ? {
              phone_number: parsePhoneNumber(otherData.phone_number, 'US')?.format('E.164') as string,
            }
          : { phone_number: null }),
        address: {
          address1: otherData.address1,
          address2: otherData.address2,
          city: otherData.city,
          state: otherData.state,
          zip_code: otherData.zip_code,
        },
      };

      if (hasContactInfoChanged(thisUser, otherData as ProviderMyProfileSubmit)) {
        updatedUser = await updateMe(updateMePayload, thisUser.id);
      }

      if (
        !thisUser.provider_profile ||
        hasProviderProfileChanged(thisUser.provider_profile, updateProfileData, reviewProfile)
      ) {
        updatedUser = thisUser.provider_profile
          ? await saveProviderProfile(updateProfileData as ProviderProfile, thisUser.id)
          : await createProviderProfile(updateProfileData as ProviderProfile, thisUser.id);
      }

      if (thisUser && reviewProfile) {
        const promises: Promise<any>[] = [];
        const currentLicenses = thisUser.provider_profile
          ? thisUser.provider_profile.licenses
          : updatedUser?.provider_profile?.licenses;

        const updatedLicenses = otherData.licences;

        if (currentLicenses) {
          updatedLicenses.forEach((updatedLicense) => {
            const foundLicense = currentLicenses.find((license) => license.id === updatedLicense.id);

            if (foundLicense) {
              if (hasLicenseChanged(foundLicense, updatedLicense)) {
                promises.push(
                  updateProviderLicense(
                    {
                      state: updatedLicense.state,
                      number: updatedLicense.number,
                      expiry_date: updatedLicense.expiry_date,
                      insurance_carriers: updatedLicense.insurance_carriers.map((plan) => {
                        const { _tempId, ...planPayload } = plan as typeof plan & { _tempId?: string };

                        return {
                          ...planPayload,
                          insurance_carrier_id: Number(plan.insurance_carrier_id),
                          effective_date: moment(plan.effective_date).format('YYYY-MM-DD'),
                          recredential_date: moment(plan.recredential_date).format('YYYY-MM-DD'),
                        };
                      }),
                    },
                    foundLicense.id!,
                    thisUser.id,
                  ),
                );
              }
            } else {
              promises.push(
                createProviderLicense(
                  {
                    state: updatedLicense.state,
                    number: updatedLicense.number,
                    expiry_date: updatedLicense.expiry_date,
                    insurance_carriers: updatedLicense.insurance_carriers.map((plan) => {
                      const { _tempId, ...planPayload } = plan as typeof plan & { _tempId?: string };

                      return {
                        ...planPayload,
                        insurance_carrier_id: Number(plan.insurance_carrier_id),
                        effective_date: moment(plan.effective_date).format('YYYY-MM-DD'),
                        recredential_date: moment(plan.recredential_date).format('YYYY-MM-DD'),
                      };
                    }),
                  },
                  thisUser.id,
                ),
              );
            }
          });

          for (const currentLicense of currentLicenses) {
            const stillExists = updatedLicenses.some((license) => license.id === currentLicense.id);
            if (!stillExists) {
              promises.push(deleteProviderLicense(currentLicense.id!, thisUser.id));
            }
          }
        }

        const submittedClientTagIds = ((otherData as ProviderMyProfileSubmit).client_tags || [])
          .map((clientTag) => Number(clientTag.id))
          .filter((id) => Number.isInteger(id) && id > 0)
          .sort((a, b) => a - b);

        const currentClientTagIds = (thisUser.clients || [])
          .map((client) => Number(client.id))
          .filter((id) => Number.isInteger(id) && id > 0)
          .sort((a, b) => a - b);

        const clientTagsChanged = submittedClientTagIds.join(',') !== currentClientTagIds.join(',');

        const submittedDivisionIds = ((otherData as ProviderMyProfileSubmit).client_divisions || [])
          .map((division) => Number(division.id))
          .filter((id) => Number.isInteger(id) && id > 0)
          .sort((a, b) => a - b);

        const currentDivisionIds = (thisUser.divisions || [])
          .map((division) => Number(division.id))
          .filter((id) => Number.isInteger(id) && id > 0)
          .sort((a, b) => a - b);

        const divisionsChanged = submittedDivisionIds.join(',') !== currentDivisionIds.join(',');

        // A locked user's tags go back exactly as they hold them: the server lets an unchanged set
        // through and scopes only the divisions this save changes.
        if (clientTagsLocked ? divisionsChanged : clientTagsChanged || divisionsChanged) {
          promises.push(
            updateUserClientTag(
              Number(thisUser.id),
              {
                client_ids: clientTagsLocked ? currentClientTagIds : submittedClientTagIds,
                client_division_ids: submittedDivisionIds,
              },
              userInfo.role.name,
            ),
          );
        }

        if (
          (userInfo.role.name === UserRoleName.Admin || userInfo.role.name === UserRoleName.SuperAdmin) &&
          (otherData as ProviderMyProfileSubmit).network !== (thisUser.network ?? null)
        ) {
          promises.push(
            updateUserNetwork(thisUser.id, (otherData as ProviderMyProfileSubmit).network ?? null, userInfo.role.name),
          );
        }

        await Promise.all(promises);
      }

      updatedUser = reviewProfile ? await getUserById(thisUser.id) : await getCurrentProfile();

      const updatedProfileForm = mapUserToProviderProfileForm(updatedUser);

      setThisUser(updatedUser);
      setMyProfileForm(updatedProfileForm);
      reset(updatedProfileForm);
      setAvatarAction('');
      setAvatarFile(null);
      setAvatarPayload(null);

      if (!reviewProfile) {
        dispatch(setProviderMyProfile(structuredClone(updatedProfileForm)));
        dispatch(setUser({ userInfo: updatedUser }));
      }

      setUserLoaded(!userLoaded);
      showSnackbar('Your changes have been saved', 'success');
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setIsSavingProfile(false);
      setLoading(false);

      if (state?.needToCompleteProfile) {
        setReturnToSelectPatient(true);
      }
    }
  };

  const onCloseConfirmationModal = (): void => {
    setShowValidationAddressModal({
      isOpen: false,
      title: '',
      whatYouEntered: '',
      recommended: false,
      recommendedAddress: '',
      onClose: undefined,
      onConfirmChange: undefined,
      onKeepCurrentAddress: undefined,
    });
  };

  const updateCurrentAddress = (standardizedAddress: any, postalAddress: any, addressComponents: any): void => {
    const streetNumber = addressComponents.find(
      (addressComponent: any) => addressComponent.componentType === 'street_number',
    );
    const address1 = addressComponents.find((addressComponent: any) => addressComponent.componentType === 'route');
    const stateComponent = addressComponents.find(
      (addressComponent: any) => addressComponent.componentType === 'administrative_area_level_1',
    );

    if (postalAddress) {
      setValue('city', postalAddress.locality);
      setValue('zip_code', postalAddress.postalCode);
    }

    if (streetNumber || address1) {
      setValue('address1', `${streetNumber?.componentName?.text || ''} ${address1?.componentName?.text || ''}`.trim());
    }

    if (stateComponent) {
      setValue('state', stateComponent.componentName?.text || '');
    } else if (standardizedAddress?.state) {
      setValue('state', standardizedAddress.state);
    }
  };

  const onSubmitProfile = async (profileData: ProviderMyProfileSubmit): Promise<void> => {
    if (isManagedByD1) {
      showSnackbar('This profile is managed by Dentistry.One and cannot be edited.', 'error');
      return;
    }
    // Same groups the admin section renders: a Client Admin confined in a tag has to leave the
    // user in one of their divisions there.
    const divisionGroups = scopeDivisionGroupsToClientAdmin(
      getClientDivisionOptionGroups(
        clientTagCatalog,
        (profileData.client_tags || []).map((tag) => tag.id),
        (myProfileForm.client_divisions || []).map((d) => ({ id: String(d.id), value: d.id, label: d.name })),
      ),
      clientTagCatalog,
      divisionScopeActor,
    );
    if (
      hasUnfilledRequiredDivisionGroup(
        divisionGroups,
        (profileData.client_divisions || []).map((division) => division.id),
      )
    ) {
      showSnackbar('Select a division.', 'error');
      return;
    }
    await validateAddress(
      profileData,
      getValues as UseFormGetValues<UnifiedProfileSubmit>,
      validationResponseId,
      callSaveProfile,
      setValidationResponseId,
      setShowValidationAddressModal,
      onCloseConfirmationModal,
      updateCurrentAddress,
    );
  };

  const education = watch('education') || [];
  const specialty = watch('specialty') || [];
  const profileImageSrc = image || (watch('profile_img') as string | undefined) || undefined;

  const headerDisplayName = React.useMemo(() => {
    return (
      thisUser.full_name?.trim() ||
      [thisUser.basic_info?.first_name, thisUser.basic_info?.last_name].filter(Boolean).join(' ').trim() ||
      '—'
    );
  }, [thisUser.basic_info?.first_name, thisUser.basic_info?.last_name, thisUser.full_name]);

  const displayStatus = React.useMemo(() => {
    if ((thisUser as { deleted_at?: string })?.deleted_at) return 'Deactivated';
    return thisUser.status?.trim() || 'Active';
  }, [thisUser]);

  const statusPillClass =
    displayStatus === 'Active'
      ? collectorShellStyles.statusPillReadOnlyActive
      : displayStatus === 'Deactivated'
      ? collectorShellStyles.statusPillReadOnlyDeactivated
      : collectorShellStyles.statusPillReadOnlyOther;

  const statusOptions = React.useMemo(() => {
    if (
      thisUser.role.name === UserRoleName.Provider &&
      displayStatus !== 'Pending' &&
      displayStatus !== 'Deactivated'
    ) {
      return [
        { id: 'pending', name: 'Pending' },
        { id: 'deactivated', name: 'Deactivated' },
      ];
    }
    if (thisUser.role.name === UserRoleName.Provider && displayStatus === 'Pending') {
      return [
        { id: 'active', name: 'Active' },
        { id: 'deactivated', name: 'Deactivated' },
      ];
    }
    if (displayStatus !== 'Deactivated') {
      return [{ id: 'deactivated', name: 'Deactivated' }];
    }
    return [{ id: 'active', name: 'Active' }];
  }, [displayStatus, thisUser.role.name]);

  const canCreateCase = thisUser.role.name === UserRoleName.Patient || thisUser.role.name === UserRoleName.Dependent;
  const isAdminRole = useMemo(
    () =>
      userInfo.role.name === UserRoleName.Admin ||
      userInfo.role.name === UserRoleName.SuperAdmin ||
      userInfo.role.name === UserRoleName.OrganizationAdmin,
    [userInfo.role.name],
  );
  /**
   * A Client Admin can view, but never manage, a Provider who carries a D1 Network marker:
   * every field is disabled and the account actions (status, invite, password) are hidden.
   */
  const isManagedByD1 = useMemo(
    () => isNetworkManagedByD1(userInfo.role.name, thisUser.network),
    [userInfo.role.name, thisUser.network],
  );
  // A Client Admin never changes a user's client tags, so the tag field is read-only for them and
  // the save sends the tags back as the server holds them; divisions under their own tag stay editable.
  const clientTagsLocked = Boolean(reviewProfile) && userInfo.role.name === UserRoleName.OrganizationAdmin;
  const openUserHeaderMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setUserHeaderMenuAnchor(event.currentTarget);
  };

  const closeUserHeaderMenu = (): void => {
    setUserHeaderMenuAnchor(null);
  };

  const openStatusMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setStatusMenuAnchor(event.currentTarget);
  };

  const closeStatusMenu = (): void => {
    setStatusMenuAnchor(null);
  };

  const getInvitationDomain = (): string => {
    const organizations = thisUser.organizations || [];
    const org =
      organizations.find((organization) => organization.id !== 'dentistry-one') ??
      organizations.find((organization) => organization.id === 'dentistry-one');
    const currentDomain = window.location.hostname;
    const fallbackDomain =
      currentDomain === 'localhost' || currentDomain.startsWith('localhost') ? 'develop.dentistry.one' : currentDomain;

    if (!org) return fallbackDomain;

    const divisionDomain = org.divisions?.[0]?.domain;
    const practiceDomain = org.practices?.[0]?.domain;

    return divisionDomain || practiceDomain || fallbackDomain;
  };

  const inviteUser = async (): Promise<void> => {
    if (!userId) return;
    setIsInvitedUser(true);
    try {
      const updatedUser = await reinviteUserById(
        userId,
        { frontend_website: `https://${getInvitationDomain()}` },
        userInfo.role.name,
      );
      setThisUser({ ...thisUser, status: updatedUser?.status, email: updatedUser?.email });
    } catch (error: unknown) {
      const responseMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (responseMessage?.includes('User has no email') || responseMessage?.includes('12209-3')) {
        setDisplayEmailModal(true);
        return;
      }
      displayErrorDetails(error);
    } finally {
      setIsInvitedUser(false);
    }
  };

  const handleStatusClick = async (status: string): Promise<void> => {
    if (status === 'deactivated' || status === 'pending') {
      setChangeUserStatusFlag?.(status);
    } else if (userId) {
      try {
        const userProfile = await activateUserById(Number(userId));
        setThisUser({ ...thisUser, status: userProfile?.status ?? thisUser.status, deleted_at: null });
        toast.success('User has been activated.', { className: 'fs-unmask' });
      } catch (error: unknown) {
        displayErrorDetails(error);
      }
    }
    closeStatusMenu();
  };

  const renderPrimaryAccount = (): JSX.Element => {
    return (
      <div className={styles.ProfileDiv}>
        {!reviewProfile && (
          <div className={styles.profileAvatar}>
            <ProfileImage
              reviewProfile={false}
              id={myProfileForm.providerID?.toString()}
              setCurrentAvatarURL={setCurrentAvatarURL}
              setAvatarFile={setAvatarFile}
              setAvatarPayload={setAvatarPayload}
              setAvatarAction={setAvatarAction}
              triggerProfileAvatar={triggerProfileAvatar}
              setImage={setImage}
              image={profileImageSrc}
              errors={errors}
              size="80px"
            />
          </div>
        )}

        <form ref={formRef} className={styles.formContainer} onSubmit={handleSubmit(onSubmitProfile, onInvalid)}>
          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <TextField
                label="First name"
                data-testid="profile-first-name"
                errors={errors.first_name ? errors : {}}
                name="first_name"
                value={watch('first_name')}
                onChange={(e: any) => {
                  if (errors.first_name) clearErrors('first_name');
                  setValue('first_name', e.target.value, { shouldValidate: true });
                }}
                disabled={userInfo.role.name === UserRoleName.Provider || isManagedByD1}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>

            <div className={styles.inputWrap}>
              <TextField
                label="Middle name (optional)"
                data-testid="profile-middle-name"
                errors={errors.middle_name ? errors : {}}
                name="middle_name"
                value={watch('middle_name')}
                onChange={(e: any) => {
                  if (errors.middle_name) clearErrors('middle_name');
                  setValue('middle_name', e.target.value, { shouldValidate: true });
                }}
                disabled={userInfo.role.name === UserRoleName.Provider || isManagedByD1}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>

            <div className={styles.inputWrap}>
              <TextField
                label="Last name"
                data-testid="profile-last-name"
                errors={errors.last_name ? errors : {}}
                name="last_name"
                value={watch('last_name')}
                onChange={(e: any) => {
                  if (errors.last_name) clearErrors('last_name');
                  setValue('last_name', e.target.value, { shouldValidate: true });
                }}
                disabled={userInfo.role.name === UserRoleName.Provider || isManagedByD1}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
          </div>
          {isAdminRole && (
            <div className={styles.inputWrap}>
              <TextField
                label="Email address"
                errors={errors.email ? errors : {}}
                data-testid="profile-email"
                name="email"
                value={watch('email')}
                onChange={(e: any) => {
                  if (errors.email) clearErrors('email');
                  setValue('email', e.target.value, { shouldValidate: true });
                }}
                disabled={isManagedByD1}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
          )}

          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <MUISelectInput
                label="Gender assigned at birth"
                data-testid="profile-gender"
                disabled={isManagedByD1}
                errors={errors.gender ? errors : {}}
                value={getValues('gender') || ''}
                name="gender"
                onChange={(e: any) => {
                  if (errors.gender) clearErrors('gender');
                  setValue('gender', e, { shouldValidate: true });
                }}
                required
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClassGender}`}
                options={genderOptions}
              />
            </div>

            <div className={styles.inputWrap}>
              <MUISelectInput
                label="Gender identity"
                data-testid="profile-gender-identity"
                disabled={isManagedByD1}
                value={watch('gender_identity') || ''}
                errors={errors.gender_identity ? errors : {}}
                name="gender_identity"
                onChange={(e: any) => {
                  if (errors.gender_identity) clearErrors('gender_identity');
                  setValue('gender_identity', e, { shouldValidate: true });
                }}
                required
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClassIdentity}`}
                options={genderIdentity}
              />
            </div>

            <div className={styles.inputWrap}>
              <MUISelectInput
                label="Preferred pronouns (optional)"
                data-testid="profile-pronouns"
                disabled={isManagedByD1}
                errors={errors.pronouns ? errors : {}}
                value={watch('pronouns') || ''}
                name="pronouns"
                onChange={(e: any) => {
                  if (errors.pronouns) clearErrors('pronouns');
                  setValue('pronouns', e, { shouldValidate: true });
                }}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClassPronouns}`}
                options={pronounsOptions}
              />
            </div>
          </div>

          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <MultiSelectInputMUI
                label="Languages"
                data-testid="profile-language"
                disabled={isManagedByD1}
                errors={errors.language ? errors : {}}
                name="language"
                onChange={(selectedValues: OptionLanguage[]) => {
                  if (errors.language) clearErrors('language');
                  setValue('language', selectedValues, { shouldValidate: true, shouldDirty: true });
                }}
                withId
                value={
                  Array.isArray(watch('language')) && typeof watch('language')[0] === 'string'
                    ? (watch('language') as string[])
                    : Array.isArray(watch('language')) && typeof watch('language')[0] === 'object'
                    ? (watch('language') as OptionLanguage[])
                        .map((lang) => (lang?.id !== undefined && lang?.id !== null ? String(lang.id) : ''))
                        .filter(Boolean)
                    : []
                }
                required
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileLanguage}`}
                options={languageOptions}
              />
            </div>

            <div className={`${styles.inputWrap} ${styles.inputWrapPhoneInput}`}>
              <PhoneInput
                value={watch('phone_number') as E164Number}
                data-testid="profile-phone-number"
                disabled={isManagedByD1}
                id="phone_number"
                name="phone_number"
                onChange={(val: string | undefined) => {
                  if (val === undefined || val === '') {
                    setValue('phone_number', '', { shouldValidate: true });
                    return;
                  }

                  setValue('phone_number', val, { shouldValidate: true });
                }}
                required
                label="Phone number"
                errors={errors}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfilePhone}`}
              />
            </div>
          </div>

          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <TextField
                label="Address"
                data-testid="profile-address1"
                disabled={isManagedByD1}
                errors={errors.address1 ? errors : {}}
                name="address1"
                value={watch('address1')}
                onChange={(e: any) => {
                  if (errors.address1) clearErrors('address1');
                  setValue('address1', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileAddress}`}
              />
            </div>

            <div className={styles.inputWrap}>
              <TextField
                label="Apartment, suite, etc. (optional)"
                errors={errors.address2 ? errors : {}}
                data-testid="profile-address2"
                disabled={isManagedByD1}
                name="address2"
                value={watch('address2')}
                onChange={(e: any) => {
                  if (errors.address2) clearErrors('address2');
                  setValue('address2', e.target.value, { shouldValidate: true });
                }}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileAddress2}`}
              />
            </div>
          </div>

          <div className={`${styles.inputWrapRow} ${styles.inputWrapEqualRow}`}>
            <div className={styles.inputWrap}>
              <TextField
                label="City"
                errors={errors.city ? errors : {}}
                data-testid="profile-city"
                disabled={isManagedByD1}
                name="city"
                value={watch('city')}
                onChange={(e: any) => {
                  if (errors.city) clearErrors('city');
                  setValue('city', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>

            <div className={styles.inputWrap}>
              <MUISelectInput
                label="State"
                data-testid="profile-state"
                disabled={isManagedByD1}
                errors={errors.state ? errors : {}}
                name="state"
                onChange={(e: any) => {
                  if (errors.state) clearErrors('state');
                  setValue('state', e, { shouldValidate: true });
                }}
                value={watch('state') || ''}
                required
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                options={stateOptions}
              />
            </div>

            <div className={styles.inputWrap}>
              <TextField
                label="Zip code"
                data-testid="profile-zipcode"
                disabled={isManagedByD1}
                errors={errors.zip_code ? errors : {}}
                name="zip_code"
                value={watch('zip_code')}
                onChange={(e: any) => {
                  if (errors.zip_code) clearErrors('zip_code');
                  setValue('zip_code', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
          </div>

          <div className={`${styles.inputWrapRow} ${styles.inputWrapEqualRow}`}>
            <div className={styles.inputWrap}>
              <Autocomplete<string, true, false, true>
                multiple
                freeSolo
                disabled={isManagedByD1}
                options={[]}
                value={education}
                inputValue={educationInputValue}
                onInputChange={(_, v) => setEducationInputValue(v)}
                onChange={(_, value) => setValue('education', value, { shouldValidate: true })}
                popupIcon={<ArrowDropDownIcon />}
                clearIcon={<ClearIcon />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      sx={{
                        height: 28,
                        borderRadius: '16px',
                        backgroundColor: '#EEF5FF',
                        color: 'var(--primary-color)',
                        border: '1px solid var(--primary-color)',
                        '& .MuiChip-deleteIcon': {
                          color: 'var(--primary-color)',
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => {
                  const { InputProps, inputProps } = params;

                  return (
                    <TextField
                      name="education"
                      label="Education / Universities"
                      required
                      register={register}
                      placeholder="Type and press Enter"
                      errors={errors.education ? errors : {}}
                      wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                      inputClass={styles.profileInputClass}
                      InputProps={InputProps}
                      inputProps={inputProps}
                    />
                  );
                }}
              />
            </div>
          </div>

          <div className={`${styles.inputWrapRow} ${styles.inputWrapEqualRow}`}>
            <div className={styles.inputWrap}>
              <Autocomplete<SpecialtyOption, true, false, true>
                multiple
                freeSolo
                disabled={isManagedByD1}
                options={specialtyOptions as SpecialtyOption[]}
                value={specialty}
                inputValue={specialtyInputValue}
                onInputChange={(_, v) => setSpecialtyInputValue(v)}
                onChange={(_, value) => {
                  const normalized = value.map((v) => (typeof v === 'string' ? { label: v, value: v } : v));
                  setValue('specialty', normalized, { shouldValidate: true });
                }}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                popupIcon={<ArrowDropDownIcon />}
                clearIcon={<ClearIcon />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.value}
                      label={option.label}
                      sx={{
                        height: 28,
                        borderRadius: '16px',
                        backgroundColor: '#EEF5FF',
                        color: 'var(--primary-color)',
                        border: '1px solid var(--primary-color)',
                        '& .MuiChip-deleteIcon': {
                          color: 'var(--primary-color)',
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => {
                  const { InputProps, inputProps } = params;

                  return (
                    <TextField
                      name="specialty"
                      label="Specialties (select up to 6)"
                      required
                      register={register}
                      placeholder="Type and press Enter"
                      errors={errors.specialty ? errors : {}}
                      wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                      inputClass={styles.profileInputClass}
                      InputProps={InputProps}
                      inputProps={inputProps}
                    />
                  );
                }}
              />
            </div>
          </div>

          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <TextField
                label="Additional credentials (optional)"
                name="additional_credentials"
                data-testid="profile-additional_credentials"
                disabled={isManagedByD1}
                onChange={(e: any) => {
                  if (errors.additional_credentials) clearErrors('additional_credentials');
                  setValue('additional_credentials', e.target.value, { shouldValidate: true });
                }}
                errors={errors.additional_credentials ? errors : {}}
                value={watch('additional_credentials')}
                multiline
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                inputClass={styles.profileInputClass}
              />
            </div>
          </div>

          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <MuiLongTextField
                label="Biography"
                name="biography"
                data-testid="profile-biography"
                disabled={isManagedByD1}
                onChange={(e: any) => {
                  if (errors.biography) clearErrors('biography');
                  setValue('biography', e.target.value, { shouldValidate: true });
                }}
                errors={errors.biography ? errors : {}}
                value={watch('biography')}
                multiline
                required
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                inputClass={styles.profileInputClass}
              />
            </div>
          </div>

          {!isManagedByD1 && (
            <div className={styles.buttonDiv}>
              <Button
                type="submit"
                variant="contained"
                data-testid="profile-save-changes"
                className={`${loading ? styles.inActiveBtn : ''} fs-unmask ${styles.formBtn}`}
                style={{ borderRadius: '32px', height: '42px', width: 'auto' }}
                disabled={(isEqual(myProfileForm, getValues()) && avatarAction === '') || isSavingProfile}
              >
                Save changes
              </Button>

              {!reviewProfile && (
                <Button
                  variant="text"
                  data-testid="profile-reset-password"
                  color="error"
                  onClick={() => setResetPasswordModal(true)}
                  className="fs-unmask"
                >
                  Reset password
                </Button>
              )}
            </div>
          )}
        </form>
        <ProviderProfileAdminSection
          watch={watch}
          register={register}
          errors={errors}
          userRoleName={userInfo.role.name}
          clientTagsRequired={clientTagsRequired}
          myProfileForm={myProfileForm}
          insurancePlanName={insurancePlanName}
          setValue={setValue as UseFormSetValue<ProviderMyProfileSubmit>}
          getValues={getValues as UseFormGetValues<ProviderMyProfileSubmit>}
          clientTagOptions={clientTagOptions}
          clientTagCatalog={clientTagCatalog}
          networkOptions={networkOptions}
          isManagedByD1={isManagedByD1}
          clientTagsLocked={clientTagsLocked}
          divisionScopeActor={divisionScopeActor}
        />
        <ValidateAddressModal
          title={showValidationAddressModal.title}
          isOpen={showValidationAddressModal.isOpen}
          recommended={showValidationAddressModal.recommended}
          recommendedAddress={showValidationAddressModal.recommendedAddress}
          whatYouEntered={showValidationAddressModal.whatYouEntered}
          onClose={showValidationAddressModal.onClose}
          onKeepCurrentAddress={showValidationAddressModal.onKeepCurrentAddress}
          onConfirmChange={showValidationAddressModal.onConfirmChange}
        />

        {resetPasswordModal && (
          <ChangePasswordModal
            isOpen={resetPasswordModal}
            onClose={() => setResetPasswordModal(false)}
            email={userInfo?.email || ''}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (isReturnToSelectPatient && state?.needToCompleteProfile) {
      roleNavigate('/select-patient', { state: { currentUserId: (Number(thisUser?.id) || 0).toString() } });
    }
  }, [isReturnToSelectPatient, state, roleNavigate, thisUser?.id]);

  if (loading || loadingUser) {
    return (
      <div className={styles.ProfileDiv}>
        <Loading fullScreen />
      </div>
    );
  }

  return (
    <div className={cn(styles.ProfileContainer, !reviewProfile && styles.ProfileContainerOwnProfile)}>
      {reviewProfile && (
        <div className={styles.reviewHeaderShell}>
          <button type="button" className={styles.reviewBackButton} onClick={() => roleNavigate(-1)}>
            <IoChevronBack aria-hidden />
            Back
          </button>

          <header className={`${collectorShellStyles.collectorEditUserHeader} ${styles.reviewHeader}`}>
            <div className={collectorShellStyles.collectorEditUserHeaderIdentity}>
              <ProfileImage
                className={styles.headerProfileImage}
                reviewProfile={reviewProfile}
                id={myProfileForm.providerID?.toString()}
                setCurrentAvatarURL={setCurrentAvatarURL}
                setAvatarFile={setAvatarFile}
                setAvatarPayload={setAvatarPayload}
                setAvatarAction={setAvatarAction}
                triggerProfileAvatar={triggerProfileAvatar}
                setImage={setImage}
                image={profileImageSrc}
                errors={errors}
                size="80px"
                isUpload={!isManagedByD1}
                disableDelete
                givingUserProfile={
                  {
                    first_name: thisUser.basic_info?.first_name || headerDisplayName.split(' ')[0] || '',
                    last_name: thisUser.basic_info?.last_name || headerDisplayName.split(' ').slice(1).join(' ') || '',
                  } as ProfileImageGivingUserProfile
                }
              />
              <div className={`${collectorShellStyles.collectorEditUserHeaderText} ${styles.reviewHeaderText}`}>
                <h1 className={`${collectorShellStyles.collectorEditUserHeaderName} ${styles.reviewHeaderName}`}>
                  {headerDisplayName}
                </h1>
                <div className={`${collectorShellStyles.userStatusRow} ${styles.reviewStatusRow}`}>
                  <span className={`${collectorShellStyles.userStatusLabel} ${styles.reviewStatusLabel}`}>
                    User status:
                  </span>
                  <button
                    type="button"
                    className={`${collectorShellStyles.statusPillReadOnly} ${statusPillClass} ${styles.statusPillButton}`}
                    aria-controls={statusMenuAnchor ? 'provider-review-user-status-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={statusMenuAnchor ? 'true' : undefined}
                    onClick={openStatusMenu}
                    disabled={isManagedByD1}
                  >
                    {displayStatus}
                    <IoChevronDown aria-hidden />
                  </button>
                  <Menu
                    id="provider-review-user-status-menu"
                    anchorEl={statusMenuAnchor}
                    open={Boolean(statusMenuAnchor)}
                    onClose={closeStatusMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status.id} onClick={() => handleStatusClick(status.id)}>
                        <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                          {status.name}
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
              </div>
            </div>
            <div className={collectorShellStyles.collectorEditUserHeaderActions}>
              {canCreateCase && (
                <button
                  type="button"
                  className={collectorShellStyles.collectorEditUserCreateCaseLink}
                  onClick={() => roleNavigate('/review-profile/patient/select')}
                >
                  <IoAdd aria-hidden />
                  Create new case
                </button>
              )}
              {thisUser.role.name !== UserRoleName.Dependent && !isManagedByD1 && (
                <>
                  <IconButton
                    aria-label="More actions"
                    aria-controls={userHeaderMenuAnchor ? 'provider-review-user-header-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={userHeaderMenuAnchor ? 'true' : undefined}
                    onClick={openUserHeaderMenu}
                  >
                    <IoEllipsisVertical />
                  </IconButton>
                  <Menu
                    id="provider-review-user-header-menu"
                    anchorEl={userHeaderMenuAnchor}
                    open={Boolean(userHeaderMenuAnchor)}
                    onClose={closeUserHeaderMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem
                      onClick={() => {
                        closeUserHeaderMenu();
                        void inviteUser();
                      }}
                      disabled={
                        thisUser.role.name === UserRoleName.Dependent ||
                        ['Active', 'Pending', 'Deactivated'].includes(thisUser.status) ||
                        isInvitedUser
                      }
                    >
                      <ListItemIcon>
                        <IoAdd />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                        Invite user
                      </ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        closeUserHeaderMenu();
                        setResetPasswordConfirmation?.({ isOpen: true, userState: 'Active' });
                      }}
                      disabled={['Deactivated', 'Not Invited', 'Invited'].includes(thisUser.status) || resetEmailSent}
                    >
                      <ListItemIcon>
                        <HttpsOutlinedIcon />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                        Reset password
                      </ListItemText>
                    </MenuItem>
                    {showUnlockAccount && (
                      <MenuItem
                        onClick={() => {
                          closeUserHeaderMenu();
                          openUnlockConfirm();
                        }}
                        disabled={!thisUser.email || isUnlocking}
                      >
                        <ListItemIcon>
                          <LockOpenOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                          Unlock account
                        </ListItemText>
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </div>
          </header>

          <Alert
            severity="info"
            className={addNewUserStyles.patientProfileNotice}
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            {isManagedByD1 ? (
              <>
                <strong>{headerDisplayName}</strong> belongs to the {getNetworkLabel(thisUser.network)}. Their profile
                is managed by Dentistry.One and can only be viewed here.
              </>
            ) : (
              <>
                You are editing <strong>{headerDisplayName}</strong>&apos;s profile.
              </>
            )}
          </Alert>

          {displayEmailModal && userId && (
            <AddEmailModal
              isOpen={displayEmailModal}
              onClose={() => setDisplayEmailModal(false)}
              userId={userId}
              thisUser={thisUser}
              setThisUser={setThisUser}
              inviteUser={inviteUser}
            />
          )}

          <ConfirmationModal
            isOpen={unlockConfirmOpen}
            title="Unlock account"
            content={`Are you sure you want to unlock ${headerDisplayName}'s account? This clears the account-lockout state so they can sign in again.`}
            onClose={closeUnlockConfirm}
            onConfirm={() => void handleUnlockAccount()}
            isSubmitDisabled={isUnlocking}
          />
        </div>
      )}
      {renderPrimaryAccount()}
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </div>
  );
};

export default Profile;
